import { AnalyticIdentifier, formatMonthYearPeriod } from '@common/neo.tax';
import {
  Alert,
  AlertModalLayout,
  Button,
  ButtonGroup,
  ButtonGroupOptions,
  colors,
  Loading,
  Stack,
  Typography,
  useModal,
} from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscPeriodLockStatus, useCanSetAscPeriodLock, useSetAscPeriodLock } from '@neo.tax/asc/hooks';
import { getMonthLockExceptionsText } from '@neo.tax/asc/utils';
import { useAnalyticId } from '@neo.tax/shared';

import { StyledModal } from './asc-period-lock-card.styled';

enum LockChoice {
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED',
}

// Each state is named on the control itself, the way the Company Info answers are, so the period
// reads as locked or unlocked without the reader having to interpret the position of a switch.
const LOCK_CHOICES: ButtonGroupOptions<LockChoice>[] = [
  { value: LockChoice.UNLOCKED, label: 'Unlocked' },
  { value: LockChoice.LOCKED, label: 'Locked' },
];

export const AscPeriodLockCard = (): ReactElement => {
  const { fiscalYearId, period, periodName } = useAscContext();
  const { error, loading, lockStatus } = useAscPeriodLockStatus({ fiscalYearId, periodName });
  const [setAscPeriodLock, { loading: saving }] = useSetAscPeriodLock();
  const canSetLock = useCanSetAscPeriodLock();
  const { isOpen: isConfirmOpen, setIsOpen: setConfirmOpen } = useModal();
  const toggleId = useAnalyticId(AnalyticIdentifier.ASC_PERIOD_LOCK_TOGGLE);

  const { end, start } = formatMonthYearPeriod(period);

  if (loading) {
    return <Loading height={80} />;
  }

  // Surface the failure rather than leaving the card spinning: without this the card renders a
  // loader forever whenever the query errors. The server strips messages from 5xx errors, so there
  // is nothing specific to show the user — the detail is in the server log.
  if (error || !lockStatus) {
    return (
      <Alert
        severity="error"
        title="Couldn't load the lock status for this period"
        subtitle="Please reload the page. If this keeps happening, contact support."
      />
    );
  }

  const isLocked = lockStatus.isLocked;
  // Only one direction of disagreement is reachable: a locked period can hold open months, but a
  // period holding a closed month is never unlocked. isPeriodPartiallyLocked names the sources.
  const monthExceptionsText = getMonthLockExceptionsText(lockStatus);

  // The mutation surfaces failures through its error toast, so a rejection here is already
  // reported; swallow it to keep the control from raising an unhandled rejection.
  const applyLock = async (nextIsLocked: boolean): Promise<void> => {
    try {
      await setAscPeriodLock({ fiscalYearId, periodName, isLocked: nextIsLocked });
    } catch {
      // Already reported by the toast above.
    }
  };

  // Locking freezes the Projects and Cost pages for everyone on the company, so it is confirmed.
  // Unlocking only restores access and applies immediately. `ButtonGroup` ignores a click on the
  // state the period is already in, so this only ever runs for a change.
  const handleLockChoice = async (choice: LockChoice): Promise<void> => {
    if (choice === LockChoice.LOCKED) {
      setConfirmOpen(true);
      return;
    }
    await applyLock(false);
  };

  const handleConfirmLock = async (): Promise<void> => {
    await applyLock(true);
    setConfirmOpen(false);
  };

  return (
    <Stack direction="column" gap={5} fullWidth>
      <Typography fontSize="sm" color={colors.gray[600]}>
        Locking prevents all users from editing the period{' '}
        <Typography fontSize="inherit" color="inherit" fontWeight="medium">{`${start} - ${end}`}</Typography>
      </Typography>
      <Stack direction="column" alignItems="end" gap={2}>
        <ButtonGroup
          id={toggleId}
          options={LOCK_CHOICES}
          value={isLocked ? LockChoice.LOCKED : LockChoice.UNLOCKED}
          disabled={saving || !canSetLock}
          onClick={handleLockChoice}
        />
        <Typography fontSize="xs" color={colors.gray[600]}>
          Only admins can control the lock
        </Typography>
      </Stack>

      {monthExceptionsText && (
        <Alert
          severity="warning"
          subtitle={
            <Typography fontSize="xs" color="inherit">
              <Typography fontSize="inherit" color="inherit" fontWeight="semibold">
                {monthExceptionsText}
              </Typography>{' '}
              Projects and Cost stay read-only while any month here is locked — select a single month to edit the months
              that are still open.
            </Typography>
          }
        />
      )}

      <StyledModal isOpen={isConfirmOpen} setIsOpen={setConfirmOpen}>
        <Stack direction="column" padding={6}>
          <AlertModalLayout
            title="Lock period"
            copy={`Are you sure you want to lock ${start} - ${end}? This will prevent all users from editing this period — the Projects and ASC 350-40 Cost pages become read-only until an admin unlocks it. Documents can still be generated.`}
            trailingContent={
              <>
                <Button size="base" variant="white" rounded="regular" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button size="base" variant="rsbry" rounded="regular" disabled={saving} onClick={handleConfirmLock}>
                  {saving ? 'Locking...' : 'Lock Period'}
                </Button>
              </>
            }
          />
        </Stack>
      </StyledModal>
    </Stack>
  );
};
